// src/components/NotificationsBell.tsx
//
// Real-time notification bell. Hydrates from REST once on login, then
// listens for live `task_notification` WS frames (via useNotificationSocket
// mounted in Layout). Renders:
//   - animated unread badge
//   - group-by-task collapsible rows
//   - time sections (Today / Yesterday / Earlier)
//   - inline actions: mark read, mute task, open task
//   - sound toggle
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertTriangle,
  AtSign,
  Ban,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  ChevronDown,
  ClipboardList,
  Clock,
  ExternalLink,
  Eye,
  ListChecks,
  MessageSquare,
  RefreshCw,
  Sparkles,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { tasksApi, TaskNotification } from "../api/tasks";
import { useAuth } from "../contexts/AuthContext";
import {
  bulkHydrate,
  clearNotifications,
  markRead,
  markAllRead,
  removeNotification,
  removeAllNotifications,
  selectNotifications,
  selectNotificationsHydrated,
  selectUnreadCount,
} from "../redux/slices/notifications";
import { getSoundEnabled, setSoundEnabled, previewNotificationSound } from "../hooks/useNotificationSocket";
import { destinationFor } from "../utils/notificationRouting";

interface NotificationBellProps {
  onUnreadCountChange?: (count: number) => void;
}

type ViewMode = "unread" | "all";
const VIEW_MODE_KEY = "notif:viewMode";
function getStoredViewMode(): ViewMode {
  if (typeof window === "undefined") return "unread";
  return window.localStorage.getItem(VIEW_MODE_KEY) === "all" ? "all" : "unread";
}

interface KindMeta {
  Icon: LucideIcon;   // refined Lucide glyph (not emoji)
  label: string;      // human label, sentence case
  tileBg: string;     // icon tile background (Teamlio token)
  tileFg: string;     // icon tile foreground / pill text
  accent: string;     // left strip + unread dot colour
}

// Brighter kind palette — Tailwind 200-level backgrounds with 800-level
// foreground & 500/600-level accent. Saturated enough to read on white
// while still harmonising with Teamlio's warm cream + forest-green chrome.
const KIND_META: Record<string, KindMeta> = {
  ASSIGNED: {
    Icon: ClipboardList,
    label: "Assigned",
    tileBg: "#d1fae5",      // emerald-200
    tileFg: "#065f46",      // emerald-800
    accent: "#10b981",      // emerald-500
  },
  MENTIONED: {
    Icon: AtSign,
    label: "Mentioned",
    tileBg: "#dbeafe",      // blue-200
    tileFg: "#1e40af",      // blue-800
    accent: "#3b82f6",      // blue-500
  },
  STATUS: {
    Icon: RefreshCw,
    label: "Status",
    tileBg: "#fde68a",      // amber-200
    tileFg: "#92400e",      // amber-800
    accent: "#f59e0b",      // amber-500
  },
  BLOCKED: {
    Icon: Ban,
    label: "Hold",
    tileBg: "#fecaca",      // red-200
    tileFg: "#991b1b",      // red-800
    accent: "#ef4444",      // red-500
  },
  COMMENT: {
    Icon: MessageSquare,
    label: "Comment",
    tileBg: "#c7d2fe",      // indigo-200
    tileFg: "#3730a3",      // indigo-800
    accent: "#6366f1",      // indigo-500
  },
  COMPLETED: {
    Icon: CheckCheck,
    label: "Completed",
    tileBg: "#a7f3d0",      // emerald-200 (slightly punchier)
    tileFg: "#065f46",      // emerald-800
    accent: "#10b981",      // emerald-500
  },
  CHECKLIST: {
    Icon: ListChecks,
    label: "Checklist",
    tileBg: "#cffafe",      // cyan-100
    tileFg: "#155e75",      // cyan-800
    accent: "#06b6d4",      // cyan-500
  },
  DUE_SOON: {
    Icon: Clock,
    label: "Due soon",
    tileBg: "#fed7aa",      // orange-200
    tileFg: "#9a3412",      // orange-800
    accent: "#f97316",      // orange-500
  },
  OVERDUE: {
    Icon: AlertTriangle,
    label: "Overdue",
    tileBg: "#fca5a5",      // red-300 (more punch — it's urgent)
    tileFg: "#7f1d1d",      // red-900
    accent: "#dc2626",      // red-600
  },
  REVIEW_REQUESTED: {
    Icon: Eye,
    label: "Review",
    tileBg: "#ddd6fe",      // violet-200
    tileFg: "#5b21b6",      // violet-800
    accent: "#8b5cf6",      // violet-500
  },
};

const FALLBACK_META: KindMeta = {
  Icon: Sparkles,
  label: "Update",
  tileBg: "#e5e7eb",        // gray-200
  tileFg: "#374151",        // gray-700
  accent: "#6b7280",        // gray-500
};

function metaFor(kind: string): KindMeta {
  return KIND_META[kind] ?? FALLBACK_META;
}

// ── Refined icon-button used in every row. Theme-native hover, no foreign
// saturation. `danger` softly tints rose on hover; `active` keeps mute on.
interface ActionButtonProps {
  title: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  danger?: boolean;
  active?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ title, onClick, children, danger, active }) => {
  const base: React.CSSProperties = {
    background: active ? "var(--gt-prio-urgent-bg)" : "transparent",
    color: active ? "var(--gt-prio-urgent-fg)" : "var(--gt-text-tertiary)",
    transition: "background var(--gt-t-fast), color var(--gt-t-fast)",
  };
  return (
    <button
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      className="p-1.5 rounded-lg"
      style={base}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.background = danger ? "var(--gt-prio-urgent-bg)" : "var(--gt-surface-2)";
        el.style.color = danger ? "var(--gt-prio-urgent-fg)" : "var(--gt-text-primary)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.background = active ? "var(--gt-prio-urgent-bg)" : "transparent";
        el.style.color = active ? "var(--gt-prio-urgent-fg)" : "var(--gt-text-tertiary)";
      }}
    >
      {children}
    </button>
  );
};

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(diff / 3_600_000);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(diff / 86_400_000);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}

type TimeBucket = "today" | "yesterday" | "earlier";

function bucketFor(dateStr: string): TimeBucket {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const t = d.getTime();
  if (t >= startOfToday) return "today";
  if (t >= startOfYesterday) return "yesterday";
  return "earlier";
}

interface TaskGroup {
  task_id: string;
  items: TaskNotification[];
  latest: TaskNotification;
  hasUnread: boolean;
}

function groupByTask(items: TaskNotification[]): TaskGroup[] {
  const map = new Map<string, TaskGroup>();
  for (const n of items) {
    let g = map.get(n.task_id);
    if (!g) {
      g = { task_id: n.task_id, items: [], latest: n, hasUnread: false };
      map.set(n.task_id, g);
    }
    g.items.push(n);
    if (!n.is_read) g.hasUnread = true;
    if (new Date(n.created_at).getTime() > new Date(g.latest.created_at).getTime()) {
      g.latest = n;
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.latest.created_at).getTime() - new Date(a.latest.created_at).getTime()
  );
}

export function NotificationBell({ onUnreadCountChange }: NotificationBellProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.id || null;

  const allNotifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const hydrated = useSelector(selectNotificationsHydrated);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mutedIds, setMutedIds] = useState<Set<string>>(new Set());
  const [soundOn, setSoundOnState] = useState<boolean>(getSoundEnabled());
  const [animating, setAnimating] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const prevUnreadRef = useRef(unreadCount);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Animated body height — measured from content so the modal smoothly
  // grows/shrinks when toggling Unread ↔ All instead of snap-resizing.
  const bodyInnerRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number | null>(null);

  // In "unread" mode: filter to is_read=false. In "all" mode: show everything
  // (read items are visually de-emphasised in the row renderer).
  const items = useMemo(
    () => (viewMode === "unread" ? allNotifications.filter(n => !n.is_read) : allNotifications),
    [allNotifications, viewMode]
  );

  // Hydrate on user change OR view-mode change. The mode controls whether we
  // ask the server for unread-only or the full recent history.
  useEffect(() => {
    if (!userId) { dispatch(clearNotifications()); return; }
    let cancelled = false;
    setLoading(true);
    const unreadOnly = viewMode === "unread";
    Promise.all([
      tasksApi.getNotifications(unreadOnly, 50),
      tasksApi.getMutedTasks().catch(() => ({ data: { task_ids: [] as string[] } })),
    ])
      .then(([nRes, mRes]) => {
        if (cancelled) return;
        dispatch(bulkHydrate(nRes.data));
        setMutedIds(new Set(mRes.data?.task_ids ?? []));
      })
      .catch(err => { if (!cancelled) console.error("Failed to load notifications:", err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, userId, viewMode]);

  const changeViewMode = (mode: ViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    if (typeof window !== "undefined") window.localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  // No document-level click-outside listener: the modal is portaled to
  // <body>, so it sits outside dropdownRef's DOM subtree. The backdrop's
  // own onClick handles "click outside to close" cleanly.

  // Bubble unread to parent.
  useEffect(() => { onUnreadCountChange?.(unreadCount); }, [unreadCount, onUnreadCountChange]);

  // Pulse the icon when a new notification arrives.
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setAnimating(true);
      const t = setTimeout(() => setAnimating(false), 900);
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  const groups = useMemo(() => groupByTask(items), [items]);

  // Re-measure the body's natural content height whenever it could
  // change, then drive the body element's `height` style. The CSS
  // transition smoothly interpolates between the old and new values,
  // so the modal expands/contracts on toggle instead of snap-resizing.
  useLayoutEffect(() => {
    if (!isOpen) {
      setBodyHeight(null);
      return;
    }
    const el = bodyInnerRef.current;
    if (!el) return;
    const cap = Math.floor(window.innerHeight * 0.62); // hard ceiling
    const next = Math.min(el.scrollHeight, cap);
    setBodyHeight((prev) => (prev === next ? prev : next));
    // Also follow content size changes (expanding a grouped row, etc.)
    // via ResizeObserver — guarded for older browsers.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const inner = bodyInnerRef.current;
      if (!inner) return;
      const target = Math.min(inner.scrollHeight, Math.floor(window.innerHeight * 0.62));
      setBodyHeight((prev) => (prev === target ? prev : target));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, groups, viewMode, loading, expanded]);
  const bucketed = useMemo(() => {
    const buckets: Record<TimeBucket, TaskGroup[]> = { today: [], yesterday: [], earlier: [] };
    for (const g of groups) buckets[bucketFor(g.latest.created_at)].push(g);
    return buckets;
  }, [groups]);

  const handleMarkRead = async (id: string) => {
    dispatch(markRead(id));
    try { await tasksApi.markNotificationRead(id); }
    catch (err) { console.error("Failed to mark as read:", err); }
  };

  const handleMarkAllRead = async () => {
    dispatch(markAllRead());
    try { await tasksApi.markAllNotificationsRead(); }
    catch (err) { console.error("Failed to mark all as read:", err); }
  };

  const handleDelete = async (notificationId: string) => {
    dispatch(removeNotification(notificationId)); // optimistic
    try { await tasksApi.deleteNotification(notificationId); }
    catch (err) { console.error("Failed to delete notification:", err); }
  };

  const handleDeleteGroup = async (taskGroup: TaskGroup) => {
    // Delete every notification in this task group.
    const ids = taskGroup.items.map(n => n.id);
    ids.forEach(id => dispatch(removeNotification(id)));
    await Promise.allSettled(ids.map(id => tasksApi.deleteNotification(id)));
  };

  const handleClearAll = async () => {
    if (typeof window !== "undefined"
        && !window.confirm("Delete all notifications? This cannot be undone.")) {
      return;
    }
    dispatch(removeAllNotifications()); // optimistic
    try { await tasksApi.deleteAllNotifications(); }
    catch (err) { console.error("Failed to clear all:", err); }
  };

  const handleOpen = (taskId: string, notifId?: string, kind?: string, body?: string) => {
    if (notifId) handleMarkRead(notifId);
    navigate(destinationFor(kind || "", taskId, body));
    setIsOpen(false);
  };

  const handleToggleMute = async (taskId: string) => {
    // Optimistic update.
    setMutedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
    try {
      const res = await tasksApi.toggleTaskMute(taskId);
      setMutedIds(prev => {
        const next = new Set(prev);
        if (res.data.muted) next.add(taskId); else next.delete(taskId);
        return next;
      });
    } catch (err) {
      // Roll back on failure.
      setMutedIds(prev => {
        const next = new Set(prev);
        if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
        return next;
      });
      console.error("Failed to toggle mute:", err);
    }
  };

  const toggleExpand = (taskId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOnState(next);
    setSoundEnabled(next);
    // Preview the chime when turning ON so user hears confirmation.
    if (next) previewNotificationSound();
  };

  // Global running index so the cascade reveal continues across buckets
  // (Today → Yesterday → Earlier) instead of resetting per section.
  // Reset each render cycle via the closure pattern below.
  const staggerRef = { i: 0 };
  const nextStagger = () => Math.min(staggerRef.i++, 10);

  const renderGroup = (g: TaskGroup) => {
    const meta = metaFor(g.latest.kind);
    const muted = mutedIds.has(g.task_id);
    const isExpanded = expanded.has(g.task_id);
    const count = g.items.length;
    const Icon = meta.Icon;
    const stagger = nextStagger();
    return (
      <div
        key={g.task_id}
        className="group/n relative transition-all gt-row-in"
        style={{
          // Body is now white — unread rows get a faint mint tint to lift
          // them from the read rows.
          background: g.hasUnread ? "rgba(15, 45, 31, 0.035)" : "transparent",
          borderBottom: "1px solid var(--gt-divider)",
          animationDelay: `${stagger * 28}ms`,
        }}
      >
        {/* Left accent strip — solid theme colour, not gradient */}
        <div
          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-opacity"
          style={{
            background: meta.accent,
            opacity: g.hasUnread ? 1 : 0,
          }}
        />

        <div
          className="relative flex gap-3.5 px-5 py-4 transition-colors"
          style={{
            opacity: g.hasUnread ? 1 : 0.82,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = "rgba(15, 45, 31, 0.045)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = "transparent";
          }}
        >
          {/* Icon tile — solid theme-tinted surface, no gradient */}
          <div
            className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center transition-transform"
            style={{
              background: meta.tileBg,
              color: meta.tileFg,
              boxShadow: "var(--gt-elev-1)",
            }}
          >
            <Icon size={19} strokeWidth={2.25} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <button
                onClick={() => handleOpen(g.task_id, g.latest.id, g.latest.kind, g.latest.body)}
                className="text-left flex-1 min-w-0"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[13.5px] leading-snug truncate"
                    style={{
                      fontWeight: g.hasUnread ? 700 : 500,
                      color: g.hasUnread ? "var(--gt-text-primary)" : "var(--gt-text-secondary)",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {g.latest.title}
                  </span>
                  <span
                    className="shrink-0 text-[10px] font-semibold px-2 py-[2px] rounded-full uppercase"
                    style={{
                      background: meta.tileBg,
                      color: meta.tileFg,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {meta.label}
                  </span>
                  {count > 1 && (
                    <span
                      className="shrink-0 text-[10px] font-bold px-2 py-[2px] rounded-full"
                      style={{
                        background: "var(--gt-surface-3)",
                        color: "var(--gt-text-secondary)",
                      }}
                    >
                      ×{count}
                    </span>
                  )}
                  {g.hasUnread && (
                    <span className="shrink-0 relative inline-flex h-1.5 w-1.5">
                      <span
                        className="absolute inline-flex h-full w-full rounded-full animate-ping"
                        style={{ background: meta.accent, opacity: 0.45 }}
                      />
                      <span
                        className="relative inline-flex h-1.5 w-1.5 rounded-full"
                        style={{ background: meta.accent }}
                      />
                    </span>
                  )}
                </div>
                <p
                  className="text-[12.5px] mt-1 line-clamp-2"
                  style={{
                    color: "var(--gt-text-secondary)",
                    lineHeight: "1.55",
                  }}
                >
                  {g.latest.body}
                </p>
                <span
                  className="gt-serif-italic text-[11.5px] mt-1.5 inline-flex items-center gap-1.5"
                  style={{ color: "var(--gt-text-tertiary)" }}
                >
                  <Clock size={11} strokeWidth={2} />
                  {formatTime(g.latest.created_at)}
                </span>
              </button>

              {/* Inline actions — restrained, no colour hints, theme-native */}
              <div className="flex items-center gap-0.5 opacity-40 group-hover/n:opacity-100 transition-opacity">
                {count > 1 && (
                  <ActionButton
                    title={isExpanded ? "Collapse" : "Expand"}
                    onClick={() => toggleExpand(g.task_id)}
                  >
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </ActionButton>
                )}
                <ActionButton
                  title={muted ? "Unmute task" : "Mute task"}
                  onClick={() => handleToggleMute(g.task_id)}
                  active={muted}
                >
                  {muted ? <BellOff size={14} /> : <Bell size={14} />}
                </ActionButton>
                {g.hasUnread && (
                  <ActionButton
                    title="Mark read"
                    onClick={() => g.items.forEach((n) => !n.is_read && handleMarkRead(n.id))}
                  >
                    <Check size={14} />
                  </ActionButton>
                )}
                <ActionButton
                  title="Open task"
                  onClick={() => handleOpen(g.task_id, g.latest.id, g.latest.kind, g.latest.body)}
                >
                  <ExternalLink size={14} />
                </ActionButton>
                <ActionButton
                  title={count > 1 ? `Delete all ${count} notifications` : "Delete notification"}
                  onClick={() => handleDeleteGroup(g)}
                  danger
                >
                  <Trash2 size={14} />
                </ActionButton>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded sub-items */}
        {isExpanded && count > 1 && (
          <div
            className="relative pl-[68px] pr-5 pb-3 space-y-1"
            style={{ background: "rgba(15, 45, 31, 0.02)" }}
          >
            <div
              className="absolute top-0 bottom-3 w-px"
              style={{
                left: 36,
                background:
                  "linear-gradient(to bottom, var(--gt-border) 0%, var(--gt-border) 70%, transparent 100%)",
              }}
            />
            {g.items.slice(1).map((n) => {
              const m = metaFor(n.kind);
              return (
                <div
                  key={n.id}
                  className="group/sub relative flex items-center gap-2 py-1.5 px-2.5 rounded-xl transition-colors"
                  style={{ background: "transparent" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = "var(--gt-surface-0)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = "transparent";
                  }}
                >
                  <span
                    className="shrink-0 w-1.5 h-1.5 rounded-full"
                    style={{ background: m.accent }}
                  />
                  <button
                    onClick={() => handleOpen(g.task_id, n.id, n.kind, n.body)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  >
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase"
                      style={{
                        background: m.tileBg,
                        color: m.tileFg,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {m.label}
                    </span>
                    <span
                      className="text-xs truncate flex-1"
                      style={{ color: "var(--gt-text-secondary)" }}
                    >
                      {n.body || n.title}
                    </span>
                    <span
                      className="gt-serif-italic text-[10px] shrink-0"
                      style={{ color: "var(--gt-text-tertiary)" }}
                    >
                      {formatTime(n.created_at)}
                    </span>
                  </button>
                  <button
                    onClick={() => handleDelete(n.id)}
                    title="Delete"
                    className="p-1 rounded-md transition-opacity opacity-0 group-hover/sub:opacity-100"
                    style={{ color: "var(--gt-text-tertiary)" }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = "var(--gt-prio-urgent-bg)";
                      el.style.color = "var(--gt-prio-urgent-fg)";
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = "transparent";
                      el.style.color = "var(--gt-text-tertiary)";
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderBucket = (label: string, list: TaskGroup[]) => {
    if (list.length === 0) return null;
    return (
      <div key={label}>
        <div
          className="sticky top-0 z-10 px-5 py-2.5 flex items-baseline gap-2.5"
          style={{
            background: "var(--gt-surface-0)",   // matches white body
            borderBottom: "1px solid var(--gt-divider)",
          }}
        >
          <span
            className="text-[10px] font-bold uppercase"
            style={{
              color: "var(--gt-primary)",
              letterSpacing: "var(--gt-letter-eyebrow)",
            }}
          >
            {label}
          </span>
          <span
            className="gt-serif-italic text-[11px]"
            style={{ color: "var(--gt-text-tertiary)" }}
          >
            {list.length} {list.length === 1 ? "item" : "items"}
          </span>
        </div>
        {list.map(renderGroup)}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button — theme-native, no gradient */}
      <button
        onClick={() => setIsOpen(o => !o)}
        className="relative p-2 rounded-xl transition-colors"
        style={{
          color: "var(--gt-text-primary)",
          background: "transparent",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-primary-container)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
        aria-label="Notifications"
      >
        <Bell size={20} strokeWidth={2.25} className={animating ? "animate-bell-shake" : ""} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] text-[10px] font-bold rounded-full flex items-center justify-center px-1"
            style={{
              background: "var(--gt-prio-urgent-dot)",
              color: "#ffffff",
              boxShadow: "0 0 0 2px var(--gt-surface-0)",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        {unreadCount > 0 && animating && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full animate-ping opacity-60"
            style={{ background: "var(--gt-prio-urgent-dot)" }}
          />
        )}
      </button>

      {/* Dialog — portaled to <body> so it escapes any ancestor's
          containing block (the topbar's stacking context was clipping
          the backdrop, leaving the sidebar visible "in front"). The
          backdrop and centring wrapper are a single layer so clicks on
          the dim area reliably reach the close handler. */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[110] overflow-y-auto flex items-start sm:items-center justify-center p-3 sm:p-4"
          style={{
            background: "rgba(6, 24, 18, 0.45)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
          onClick={() => setIsOpen(false)}
        >
          <div
            className="gt-themed relative w-full max-w-[95%] sm:max-w-lg md:max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col"
            style={{
              background: "var(--gt-surface-0)",     // white body
              borderRadius: "var(--gt-radius-modal)",
              boxShadow: "var(--gt-elev-5)",
              border: "1px solid var(--gt-border)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
                {/* Header — solid lighter forest, single uniform tone */}
                <div
                  className="relative px-5 sm:px-6 py-4 sm:py-5"
                  style={{
                    background: "#3a6e54",
                    color: "var(--gt-on-primary)",
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3.5">
                      <div
                        className="w-11 h-11 rounded-2xl flex items-center justify-center"
                        style={{
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.16)",
                        }}
                      >
                        <Bell size={20} strokeWidth={2.25} />
                      </div>
                      <div>
                        <h2
                          className="text-[18px] font-bold leading-tight"
                          style={{ color: "var(--gt-on-primary)", letterSpacing: "-0.018em" }}
                        >
                          Notifications
                        </h2>
                        <p
                          className="gt-serif-italic text-[12px] mt-0.5"
                          style={{ color: "rgba(255,255,255,0.72)" }}
                        >
                          {unreadCount === 0 ? "All caught up" : `${unreadCount} unread ${unreadCount === 1 ? "item" : "items"}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={toggleSound}
                        title={soundOn ? "Mute sound" : "Enable sound"}
                        className="p-2 rounded-xl transition-colors"
                        style={{ color: "rgba(255,255,255,0.85)" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                      >
                        {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                      </button>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 rounded-xl transition-colors"
                        style={{ color: "rgba(255,255,255,0.85)" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  {/* View-mode segmented — sliding-pill indicator. The white
                       pill smoothly glides between options on click, and the
                       button label colour crossfades alongside. */}
                  <div
                    className="relative inline-flex mt-4 p-1 rounded-full"
                    style={{
                      background: "rgba(0,0,0,0.22)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {/* Sliding indicator pill */}
                    <span
                      aria-hidden
                      className="absolute top-1 bottom-1 rounded-full"
                      style={{
                        background: "var(--gt-on-primary)",
                        width: "calc(50% - 4px)",
                        left: 4,
                        transform: viewMode === "all" ? "translateX(100%)" : "translateX(0)",
                        transition: "transform 320ms cubic-bezier(0.33, 1, 0.68, 1)",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                      }}
                    />
                    <button
                      onClick={() => changeViewMode("unread")}
                      className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
                      style={{
                        color: viewMode === "unread" ? "var(--gt-primary)" : "rgba(255,255,255,0.82)",
                        letterSpacing: "0.02em",
                        transition: "color 220ms cubic-bezier(0.33, 1, 0.68, 1)",
                      }}
                    >
                      Unread{unreadCount > 0 ? ` · ${unreadCount}` : ""}
                    </button>
                    <button
                      onClick={() => changeViewMode("all")}
                      className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
                      style={{
                        color: viewMode === "all" ? "var(--gt-primary)" : "rgba(255,255,255,0.82)",
                        letterSpacing: "0.02em",
                        transition: "color 220ms cubic-bezier(0.33, 1, 0.68, 1)",
                      }}
                    >
                      All
                    </button>
                  </div>
                </div>

                {/* Body — height is driven by content via the layout effect
                       above, with a CSS transition so the modal grows or
                       shrinks smoothly when toggling Unread ↔ All. */}
                <div
                  className="overflow-y-auto relative"
                  style={{
                    height: bodyHeight !== null ? `${bodyHeight}px` : undefined,
                    transition: "height 360ms cubic-bezier(0.33, 1, 0.68, 1)",
                  }}
                >
                  {/* Tiny "refreshing" bar shown during mode-change refetch.
                       Absolutely positioned so it doesn't affect the measured
                       content height. */}
                  {loading && hydrated && (
                    <div
                      aria-hidden
                      className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-20 pointer-events-none"
                      style={{ background: "transparent" }}
                    >
                      <span
                        className="absolute inset-y-0 left-0 w-1/3 gt-refresh-bar"
                        style={{ background: "var(--gt-primary)" }}
                      />
                    </div>
                  )}
                  {/* Inner wrapper carries the content ref so the layout
                       effect always measures the natural scrollHeight,
                       independent of the body's animated height. */}
                  <div ref={bodyInnerRef}>
                  {loading && !hydrated ? (
                    <div className="flex justify-center items-center py-12">
                      <div
                        className="animate-spin rounded-full h-8 w-8"
                        style={{
                          borderTop: "2px solid var(--gt-primary)",
                          borderRight: "2px solid var(--gt-primary)",
                          borderBottom: "2px solid transparent",
                          borderLeft: "2px solid transparent",
                        }}
                      />
                    </div>
                  ) : groups.length === 0 ? (
                    <div className="text-center py-16 px-6">
                      <div
                        className="relative w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center"
                        style={{
                          background: "var(--gt-primary-container)",
                          color: "var(--gt-primary)",
                        }}
                      >
                        <Bell size={26} strokeWidth={2} />
                      </div>
                      <p
                        className="text-[14px] font-bold"
                        style={{ color: "var(--gt-text-primary)", letterSpacing: "-0.01em" }}
                      >
                        {viewMode === "unread" ? "Nothing unread" : "No notifications yet"}
                      </p>
                      <p
                        className="gt-serif-italic text-[12.5px] mt-1"
                        style={{ color: "var(--gt-text-tertiary)" }}
                      >
                        {viewMode === "unread"
                          ? "You're all caught up."
                          : "Activity will show up here."}
                      </p>
                      {viewMode === "unread" && (
                        <button
                          onClick={() => changeViewMode("all")}
                          className="text-[12px] font-semibold mt-4 px-4 py-1.5 rounded-full transition-colors"
                          style={{
                            background: "var(--gt-primary)",
                            color: "var(--gt-on-primary)",
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-primary-hover)"; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-primary)"; }}
                        >
                          View all notifications
                        </button>
                      )}
                    </div>
                  ) : (
                    // key={viewMode} remounts on toggle → triggers list fade-in.
                    // Each group inside renderBucket animates in with a tiny
                    // staggered delay for an editorial cascade reveal.
                    <div key={viewMode} className="gt-list-in">
                      {renderBucket("Today",     bucketed.today)}
                      {renderBucket("Yesterday", bucketed.yesterday)}
                      {renderBucket("Earlier",   bucketed.earlier)}
                    </div>
                  )}
                  </div>{/* /bodyInnerRef */}
                </div>

                {/* Footer */}
                {groups.length > 0 && (
                  <div
                    className="px-5 py-3 flex items-center justify-between gap-2"
                    style={{
                      background: "var(--gt-surface-0)",   // matches white body
                      borderTop: "1px solid var(--gt-border)",
                    }}
                  >
                    <button
                      onClick={handleClearAll}
                      className="px-3 py-1.5 text-[12.5px] font-semibold rounded-full flex items-center gap-2 transition-colors"
                      style={{
                        color: "var(--gt-prio-urgent-fg)",
                        background: "transparent",
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-prio-urgent-bg)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >
                      <Trash2 size={13} />
                      Clear all
                    </button>
                    {unreadCount > 0 && (
                      <button
                        onClick={async () => { await handleMarkAllRead(); }}
                        className="px-4 py-1.5 text-[12.5px] font-semibold rounded-full flex items-center gap-2 transition-colors"
                        style={{
                          background: "var(--gt-primary)",
                          color: "var(--gt-on-primary)",
                          boxShadow: "var(--gt-glow-soft)",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-primary-hover)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--gt-primary)"; }}
                      >
                        <CheckCheck size={14} />
                        Mark all as read
                      </button>
                    )}
                  </div>
                )}
            </div>
          </div>,
          document.body,
      )}

      <style>{`
        @keyframes bell-shake {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          45% { transform: rotate(8deg); }
          60% { transform: rotate(-6deg); }
          75% { transform: rotate(3deg); }
        }
        .animate-bell-shake { animation: bell-shake 0.9s ease-in-out; transform-origin: top center; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        /* ── Tab-switch cascade reveal ─────────────────────────────────
           When the user toggles Unread ↔ All, the list container is
           remounted (key={viewMode}). gt-list-in fades the wrapper in;
           gt-row-in cascades each row with a small index-based delay
           so notifications sweep in like a deck of cards.
           ────────────────────────────────────────────────────────────── */
        @keyframes gt-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gt-fade-up-row {
          from { opacity: 0; transform: translateY(6px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
        .gt-list-in {
          animation: gt-fade-up 220ms cubic-bezier(0.33, 1, 0.68, 1) both;
        }
        .gt-row-in {
          animation: gt-fade-up-row 320ms cubic-bezier(0.33, 1, 0.68, 1) both;
          will-change: opacity, transform;
        }

        /* Top sliver progress bar shown while a mode-change refetch is
           in flight (we already have data, so we keep the list visible
           and just hint at the fresh fetch). */
        @keyframes gt-refresh-sweep {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(150%); }
        }
        .gt-refresh-bar {
          animation: gt-refresh-sweep 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }

        /* Respect users who've opted out of motion. */
        @media (prefers-reduced-motion: reduce) {
          .gt-list-in, .gt-row-in, .gt-refresh-bar, .animate-bell-shake {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
